import { useEffect, useMemo, useState } from 'react'
import { Search, RotateCcw } from 'lucide-react'
import {
  ExcelEditableTable,
  type ExcelColumn,
} from '@/components/table/ExcelEditableTable'
import { DateInput } from '@/components/forms/DateInput'
import {
  getPoReceiptHistory,
  type PoReceiptHistoryRow,
} from '@/services/procurement'
import { formatDate } from '@/lib/dateFormat'
import { formatVnNumber } from './poWorkflow'

export interface PoDeliveryHistoryTableProps {
  poId?: string
  onPoClick?: (poId: string) => void
}

export interface DeliveryHistoryTableRow extends Record<string, unknown> {
  id: string
  po_id: string
  po_code: string
  supplier_id: string
  supplier_name: string
  material_id: string
  material_code: string
  material_name: string
  received_at: string
  received_date: string
  received_qty: string
  received_qty_num: number
  short_qty: string
  short_qty_num: number
  order_qty: string
  order_qty_num: number
  order_uom: string
}

export function PoDeliveryHistoryTable({
  poId,
  onPoClick,
}: PoDeliveryHistoryTableProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<PoReceiptHistoryRow[]>([])

  // Filters
  const [search, setSearch] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  useEffect(() => {
    let active = true
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const rows = await getPoReceiptHistory(poId)
        if (active) {
          setData(rows)
        }
      } catch (err) {
        if (active) {
          setError(
            err instanceof Error
              ? err.message
              : 'Không tải được lịch sử nhận hàng',
          )
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => {
      active = false
    }
  }, [poId])

  // Transform raw receipt history rows
  const tableRows: DeliveryHistoryTableRow[] = useMemo(() => {
    return data.map((r) => {
      const orderQtyNum = parseFloat(r.order_qty) || 0
      const recQtyNum = parseFloat(r.received_qty) || 0
      const shortQtyNum = parseFloat(r.short_qty) || 0
      const isShortClosed =
        (r.short_resolution === 'accept' || r.short_resolution === 'cancel_rest') &&
        shortQtyNum > 0

      const recDate = r.received_at ? r.received_at.slice(0, 10) : ''

      return {
        id: r.id,
        po_id: r.po_id,
        po_code: r.po_code,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        material_id: r.material_id,
        material_code: r.material_code,
        material_name: r.material_name,
        received_at: r.received_at,
        received_date: recDate,
        received_qty: `${formatVnNumber(recQtyNum)} ${r.order_uom}`,
        received_qty_num: recQtyNum,
        short_qty: isShortClosed
          ? `${formatVnNumber(shortQtyNum)} ${r.order_uom}`
          : '—',
        short_qty_num: shortQtyNum,
        order_qty: `${formatVnNumber(orderQtyNum)} ${r.order_uom}`,
        order_qty_num: orderQtyNum,
        order_uom: r.order_uom,
      }
    })
  }, [data])

  // Unique suppliers for filter
  const suppliers = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of tableRows) {
      if (r.supplier_name) {
        map.set(r.supplier_name, r.supplier_name)
      }
    }
    return Array.from(map.values()).sort()
  }, [tableRows])

  // Filtered rows
  const filteredRows = useMemo(() => {
    return tableRows.filter((row) => {
      // 1. Search (PO code, material name, material code)
      if (search.trim()) {
        const q = search.trim().toLowerCase()
        const matchPo = row.po_code.toLowerCase().includes(q)
        const matchMatName = row.material_name.toLowerCase().includes(q)
        const matchMatCode = row.material_code.toLowerCase().includes(q)
        if (!matchPo && !matchMatName && !matchMatCode) return false
      }

      // 2. Supplier filter
      if (selectedSupplier && row.supplier_name !== selectedSupplier) {
        return false
      }

      // 3. Date range (from / to on received_date)
      if (fromDate && row.received_date < fromDate) {
        return false
      }
      if (toDate && row.received_date > toDate) {
        return false
      }

      return true
    })
  }, [
    tableRows,
    search,
    selectedSupplier,
    fromDate,
    toDate,
  ])

  function handleResetFilters() {
    setSearch('')
    setSelectedSupplier('')
    setFromDate('')
    setToDate('')
  }

  const columns: ExcelColumn<DeliveryHistoryTableRow>[] = useMemo(() => {
    const cols: ExcelColumn<DeliveryHistoryTableRow>[] = [
      {
        key: 'id',
        label: 'STT',
        width: 50,
        align: 'center',
        readOnly: true,
        sortable: false,
        filterable: false,
        formatter: (_val: unknown, _row: DeliveryHistoryTableRow, index: number) => String(index + 1),
      },
      {
        key: 'po_code',
        label: 'PO',
        width: 130,
        sortable: true,
        filterable: true,
        align: 'left',
        renderCell: (val: unknown, row: DeliveryHistoryTableRow) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPoClick?.(row.po_id)
            }}
            className="font-semibold text-blue-700 hover:underline hover:text-blue-900 cursor-pointer text-left"
          >
            {String(val || '')}
          </button>
        ),
      },
      {
        key: 'supplier_name',
        label: 'Nhà cung cấp',
        width: 180,
        sortable: true,
        filterable: true,
        align: 'left',
      },
      {
        key: 'material_name',
        label: 'Vật tư',
        width: 240,
        sortable: true,
        filterable: true,
        align: 'left',
        renderCell: (val: unknown) => (
          <div className="font-medium text-slate-900 truncate">
            {String(val || '')}
          </div>
        ),
      },
      {
        key: 'received_date',
        label: 'Ngày nhận',
        width: 120,
        sortable: true,
        filterable: true,
        align: 'center',
        formatter: (val: unknown) => formatDate(val as string) || '—',
      },
      {
        key: 'received_qty',
        label: 'SL đã nhận',
        type: 'number',
        width: 130,
        sortable: true,
        align: 'right',
        formatter: (_val: unknown, row: DeliveryHistoryTableRow) => row.received_qty,
      },
      {
        key: 'short_qty',
        label: 'Đã hủy (nếu có)',
        type: 'number',
        width: 130,
        sortable: true,
        align: 'right',
        formatter: (_val: unknown, row: DeliveryHistoryTableRow) => row.short_qty,
      },
    ]

    return cols
  }, [onPoClick])

  return (
    <div className="space-y-3">
      {/* Toolbar Filters */}
      <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-[var(--shadow-level-1)]">
        <div className="flex flex-wrap items-center gap-2.5 text-xs">
          {/* Search box */}
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm PO hoặc vật tư..."
              className="w-full rounded border border-[var(--color-border)] py-1.5 pl-8 pr-2.5 text-xs focus:border-[var(--color-primary)] focus:outline-none"
            />
          </div>

          {/* Supplier filter */}
          {!poId && (
            <div className="w-48">
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="w-full rounded border border-[var(--color-border)] py-1.5 px-2 text-xs focus:border-[var(--color-primary)] focus:outline-none"
              >
                <option value="">Tất cả Nhà cung cấp</option>
                {suppliers.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range */}
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Từ:</span>
            <div className="w-28">
              <DateInput
                value={fromDate}
                onChange={(val) => setFromDate(val)}
                className="py-1 px-2 text-xs"
              />
            </div>
            <span className="text-slate-500">Đến:</span>
            <div className="w-28">
              <DateInput
                value={toDate}
                onChange={(val) => setToDate(val)}
                className="py-1 px-2 text-xs"
              />
            </div>
          </div>

          {/* Reset button */}
          {(search || selectedSupplier || fromDate || toDate) && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition"
              title="Đặt lại bộ lọc"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Xóa lọc</span>
            </button>
          )}

          <div className="ml-auto text-xs text-slate-500 tabular-nums">
            {filteredRows.length} / {tableRows.length} lượt nhận
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center text-xs text-slate-400">
          Đang tải lịch sử nhận hàng...
        </div>
      ) : error ? (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {error}
        </div>
      ) : (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
          <ExcelEditableTable<DeliveryHistoryTableRow>
            columns={columns}
            data={filteredRows}
            onChange={() => {}}
            emptyRowFactory={() => ({} as DeliveryHistoryTableRow)}
            readOnly={true}
            emptyMessage={
              tableRows.length === 0
                ? 'Chưa có lịch sử nhận hàng nào.'
                : 'Không tìm thấy lượt nhận hàng khớp với bộ lọc.'
            }
            storageKey={poId ? `po-receipt-hist-${poId}` : 'po-receipt-hist-all'}
          />
        </div>
      )}
    </div>
  )
}
